import { classifyPollFailure } from "./DateAuditEvent"
import { DateOcrExtractionResult } from "./DateOcrExtractionProvider"

/**
 * One automatic date capture polling session, plus the config and contracts it
 * needs. A timer-driven state machine, deliberately free of React: it owns
 * timers, attempt counting and the decision to stop, and nothing else.
 */

/**
 * PROVISIONAL: this value, DEFAULT_MAX_POLL_ATTEMPTS and
 * DEFAULT_POLL_TIMEOUT_MS are placeholders until the PSA (SidelineApp/Conan)
 * team confirms the request rate they will accept.
 */
export const DEFAULT_POLL_INTERVAL_MS = 1500

/** PROVISIONAL — see DEFAULT_POLL_INTERVAL_MS. */
export const DEFAULT_MAX_POLL_ATTEMPTS = 12

/** PROVISIONAL — see DEFAULT_POLL_INTERVAL_MS. */
export const DEFAULT_POLL_TIMEOUT_MS = 30000

/**
 * Injected rather than called directly so tests advance simulated time instead
 * of sitting through a 30 second deadline.
 */
export interface TimerScheduler {
  setTimeout(handler: () => void, timeoutMs: number): number
  clearTimeout(handle: number): void
}

export const defaultTimerScheduler: TimerScheduler = {
  // window.setTimeout, not the global, so the handle types as number.
  setTimeout: (handler, timeoutMs) => window.setTimeout(handler, timeoutMs),
  clearTimeout: (handle) => window.clearTimeout(handle),
}

/** Raw current-frame puller, handed up from CameraPopup to ImageCapture. */
export interface RawFrameSource {
  /**
   * Lossless PNG data URL of the frame current at the call instant, or null
   * when the stream is not yet producing pixels. Nothing is cached, which is
   * what makes frame freshness structural rather than a matter of
   * invalidation discipline.
   */
  grabFrame(): string | null
}

/** Encoded current-frame puller, handed up from ImageCapture to DateExtraction. */
export interface PollFrameSource {
  grabEncodedFrame(): Promise<string | null>
  /** Closes the viewfinder, releasing every MediaStream track. */
  close(): void
}

/**
 * Per-session metadata for the audit trail.
 *
 * Every field is optional and the whole object is undefined when there is
 * nothing to report, which is how the audit output stays byte-identical while
 * automatic capture is off: an absent meta contributes no keys at all.
 */
export interface PollingSessionMeta {
  pollAttemptCount?: number
  /**
   * Automatic capture was requested but the device has no live viewfinder.
   * Distinct from the worker-driven skip reasons: a device capability limit is
   * not a worker action.
   */
  autoCaptureUnavailable?: boolean
}

/** Pacing bounds, captured once at start so they cannot change mid-session. */
export interface PollingSessionConfig {
  /** Minimum gap between one request settling and the next being issued. */
  intervalMs: number
  maxAttempts: number
  /** Wall-clock bound, independent of the attempt cap. */
  timeoutMs: number
}

export interface PollingSessionOutcome {
  result: DateOcrExtractionResult
  /**
   * The frame that produced the outcome, or null when there is none — the
   * deadline-with-no-settled-request case. A null frame must not be recorded
   * as an image capture: no image was ever encoded.
   */
  frame: string | null
  meta: PollingSessionMeta
}

export interface PollingSessionDeps {
  source: PollFrameSource
  scheduler: TimerScheduler
  extract(frame: string): Promise<DateOcrExtractionResult>
  /**
   * Invoked exactly once for a session that reaches an outcome, never for one
   * that is aborted.
   */
  onOutcome(outcome: PollingSessionOutcome): void
}

export class PollingSession {
  private attemptCount = 0

  /** True from the extraction call to its settlement. Caps concurrency at one. */
  private inFlight = false

  /** Set the instant the session ends, making every late callback a no-op. */
  private ended = false

  private lastResult: DateOcrExtractionResult | null = null

  private deadlineElapsed = false

  private pollTimerHandle: number | null = null

  private deadlineTimerHandle: number | null = null

  public constructor(
    private readonly config: PollingSessionConfig,
    private readonly deps: PollingSessionDeps,
  ) {}

  public get attempts(): number {
    return this.attemptCount
  }

  public get isEnded(): boolean {
    return this.ended
  }

  /**
   * The first request goes out immediately: intervalMs is a gap between
   * requests, so there is nothing to wait for at session start.
   */
  public start(): void {
    if (this.ended || this.deadlineTimerHandle !== null) {
      return
    }
    this.deadlineTimerHandle = this.deps.scheduler.setTimeout(
      () => this.onDeadline(),
      this.config.timeoutMs,
    )
    void this.issueRequest()
  }

  /**
   * Ends the session with no outcome: the cancel, camera-error, manual entry
   * and unmount path.
   *
   * `onOutcome` deliberately does not fire. The only reason available to
   * describe a worker gesture is USER_REJECTED, which the failure policy maps
   * to DEPENDENCY, so reporting one as a result would file a backend fault
   * against a worker who pressed Cancel. Do not "fix" this into delivering a
   * result.
   *
   * `source.close()` is deliberately not called either: on every path that
   * reaches here the viewfinder is already tearing itself down.
   *
   * Safe to call repeatedly, which is what makes the double teardown signal
   * harmless — closing the viewfinder unmounts it, the unmount deregisters the
   * frame source, and the resulting abort finds a session that already ended.
   */
  public abort(): void {
    if (this.ended) {
      return
    }
    this.ended = true
    this.clearTimers()
  }

  /**
   * The encoded frame stays a local to this invocation, so peak retention is
   * one frame however many attempts a session runs.
   */
  private async issueRequest(): Promise<void> {
    if (this.ended || this.inFlight) {
      return
    }
    this.pollTimerHandle = null

    let frame: string | null
    try {
      frame = await this.deps.source.grabEncodedFrame()
    } catch (err: any) {
      // A canvas or encode fault is a property of the client pipeline, not the
      // scene, so another frame would fail the same way.
      this.finish(null, {
        success: false,
        failureReason: "ERROR",
        errorMessage: err?.message || "Frame capture failed",
      })
      return
    }
    if (this.ended) {
      return
    }
    if (!frame) {
      // Stream not producing pixels yet. No request was issued, so no attempt
      // is counted; the deadline still bounds this case.
      this.scheduleNext()
      return
    }

    this.attemptCount += 1
    this.inFlight = true

    let result: DateOcrExtractionResult
    try {
      result = await this.deps.extract(frame)
    } catch (err: any) {
      result = {
        success: false,
        failureReason: "ERROR",
        errorMessage: err?.message || "Unknown error",
      }
    }

    this.inFlight = false
    this.settle(frame, result)
  }

  /**
   * The single decision point, taken at the settlement instant of the attempt
   * being decided, while that attempt's frame is still a live local.
   *
   * Success is tested before the attempt cap, so a success arriving on the
   * final permitted attempt reports as a success rather than a cap termination.
   */
  private settle(frame: string, result: DateOcrExtractionResult): void {
    if (this.ended) {
      return
    }
    this.lastResult = result

    if (result.success && result.extractedDate) {
      this.finish(frame, result)
      return
    }
    if (classifyPollFailure(result.failureReason) === "terminal") {
      this.finish(frame, result)
      return
    }
    if (this.attemptCount >= this.config.maxAttempts) {
      this.finish(frame, result)
      return
    }
    if (this.deadlineElapsed) {
      this.finish(frame, result)
      return
    }
    this.scheduleNext()
  }

  /**
   * Measured from the settlement instant, so intervalMs is a minimum gap that
   * self-limits under provider latency rather than a fixed rate that would
   * stack requests. Never an interval.
   *
   * The handle guard makes a second concurrent schedule impossible.
   */
  private scheduleNext(): void {
    if (this.ended || this.pollTimerHandle !== null) {
      return
    }
    this.pollTimerHandle = this.deps.scheduler.setTimeout(
      () => void this.issueRequest(),
      this.config.intervalMs,
    )
  }

  /**
   * The single emission point, so no intermediate retryable result reaches the
   * caller.
   *
   * `ended` is set first, so any callback firing during teardown — including
   * the deregistration that closing the viewfinder triggers — short-circuits
   * instead of re-entering. Closing before `onOutcome` releases the camera
   * before the caller starts changing screens.
   */
  private finish(frame: string | null, result: DateOcrExtractionResult): void {
    if (this.ended) {
      return
    }
    this.ended = true
    this.clearTimers()

    const meta: PollingSessionMeta = { pollAttemptCount: this.attemptCount }
    this.deps.source.close()
    this.deps.onOutcome({ result, frame, meta })
  }

  /**
   * A mid-flight deadline deliberately does not end the session: recording it
   * and returning lets the settlement path end it while the decisive frame is
   * still a live local. The bound is therefore timeoutMs plus at most one
   * extraction latency.
   */
  private onDeadline(): void {
    if (this.ended) {
      return
    }
    this.deadlineTimerHandle = null
    this.deadlineElapsed = true

    if (this.inFlight) {
      return
    }
    const result: DateOcrExtractionResult = this.lastResult ?? {
      success: false,
      failureReason: "INCONCLUSIVE",
    }
    this.finish(null, result)
  }

  /** Safe to call twice. */
  private clearTimers(): void {
    if (this.pollTimerHandle !== null) {
      this.deps.scheduler.clearTimeout(this.pollTimerHandle)
      this.pollTimerHandle = null
    }
    if (this.deadlineTimerHandle !== null) {
      this.deps.scheduler.clearTimeout(this.deadlineTimerHandle)
      this.deadlineTimerHandle = null
    }
  }
}
